import React from 'react';
import HomeLink from './HomeLink';
import RowHeader from './RowHeader';
import { getCardThumbnailUrl } from '../../../utils/imageUrl';
import { CARD_HOVER, DISPLAY_FONT, FOCUS_RING } from './tokens';

/**
 * HOME-01 — the Guides teaser: one large card and two small ones.
 *
 * REAL GUIDES, REAL ROUTES. Every card links `/guides/<slug>` for a guide that
 * actually exists, and the kicker is the guide's OWN `category` — not the
 * design's `Buying basics` / `Diaspora` / `Market`, which are the sample data's
 * categories rather than a taxonomy this database has. Hard-coding those three
 * strings above whatever guides happen to be published would put a label on an
 * article that does not carry it. Fewer than three published guides → the
 * section collapses to what exists; none → it does not render at all, and the
 * only surviving link is "All guides →" to the index.
 *
 * WEB ONLY. The guides are Vike-rendered pages with no route in App.tsx's
 * router, so native has nothing to serve them (REL-03) and must not link them.
 * The caller gates on `isNative()`, which is a build-time constant — so this
 * whole module is dropped from the native bundle rather than hidden at runtime.
 *
 * `external` on every link for the same reason: even on web the SPA cannot
 * route to /guides, so these leave the app deliberately.
 */

export interface HomeGuide {
  slug: string;
  title: string;
  /** The guide's own category. Null → the kicker is omitted, never guessed. */
  category: string | null;
  /** Shown on the large card only, matching the design. */
  description: string | null;
  featuredImage: string | null;
}

interface GuidesTeaserProps {
  guides: HomeGuide[];
  className?: string;
}

const GuideVisual: React.FC<{ guide: HomeGuide; className: string; sizes: string }> = ({
  guide,
  className,
  sizes,
}) =>
  guide.featuredImage ? (
    <img
      src={getCardThumbnailUrl(guide.featuredImage)}
      alt=""
      loading="lazy"
      decoding="async"
      sizes={sizes}
      className={`w-full object-cover ${className}`}
    />
  ) : (
    <div
      className={`w-full ${className}`}
      style={{ background: 'linear-gradient(135deg, #134025 0%, #1B5533 100%)' }}
      aria-hidden="true"
    />
  );

const Kicker: React.FC<{ category: string | null }> = ({ category }) =>
  category ? (
    <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#7A5A0E]">
      {category}
    </div>
  ) : null;

const GuidesTeaser: React.FC<GuidesTeaserProps> = ({ guides, className = '' }) => {
  if (guides.length === 0) return null;

  const [lead, ...rest] = guides;
  const secondary = rest.slice(0, 2);

  return (
    <section className={`pt-7 lg:pt-11 ${className}`} aria-labelledby="home-guides">
      <RowHeader
        title="Guides"
        viewAllHref="/guides"
        viewAllLabel="All guides →"
        level="section"
        className="mb-3 lg:mb-4"
      />

      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <HomeLink
          to={`/guides/${lead.slug}`}
          external
          className={`block overflow-hidden rounded-[14px] border border-[#E7E6E6] bg-white ${CARD_HOVER} ${FOCUS_RING}`}
        >
          <GuideVisual guide={lead} className="h-[200px] lg:h-[250px]" sizes="(max-width: 1023px) 100vw, 600px" />
          <div className="px-5 pb-5 pt-4">
            <Kicker category={lead.category} />
            <div
              className="mt-1 text-[19px] font-extrabold tracking-[-0.02em] text-[#134025] lg:text-[22px]"
              style={{ fontFamily: DISPLAY_FONT }}
            >
              {lead.title}
            </div>
            {lead.description && (
              <div className="mt-1.5 text-[13.5px] text-[#666666]">{lead.description}</div>
            )}
          </div>
        </HomeLink>

        {secondary.length > 0 && (
          <div className="flex flex-col gap-4">
            {secondary.map((guide) => (
              <HomeLink
                key={guide.slug}
                to={`/guides/${guide.slug}`}
                external
                className={`flex flex-1 items-center gap-3.5 rounded-[14px] border border-[#E7E6E6] bg-white p-3.5 ${CARD_HOVER} ${FOCUS_RING}`}
              >
                <div className="h-[96px] w-[130px] flex-none overflow-hidden rounded-[9px]">
                  <GuideVisual guide={guide} className="h-full" sizes="130px" />
                </div>
                <div className="min-w-0">
                  <Kicker category={guide.category} />
                  <div className="mt-1 text-[15.5px] font-bold tracking-[-0.01em] text-[#134025]">
                    {guide.title}
                  </div>
                </div>
              </HomeLink>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default GuidesTeaser;
